import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Settings,
  Instagram,
  Twitter,
  Youtube,
  MapPin,
  Star,
  Shield,
  Edit,
  LogOut,
  Flame,
  MessageCircle,
  User,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { AltalabLogo } from "../components/AltalabLogo";
import BottomNavigation from "../components/BottomNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  const isOwnProfile = !userId || userId === currentUser?.id;

  useEffect(() => {
    if (isOwnProfile) {
      setProfile(currentUser);
      fetchRatings(currentUser?.id);
      setLoading(false);
    } else {
      fetchProfile();
    }
  }, [userId, currentUser]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(
        `${API}/users/${userId}?Authorization=Bearer ${token}`
      );
      setProfile(response.data);
      fetchRatings(userId);
    } catch (error) {
      toast.error("Erro ao carregar perfil");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async (id) => {
    if (!id) return;
    try {
      const response = await axios.get(
        `${API}/ratings/user/${id}?Authorization=Bearer ${token}`
      );
      setRatings(response.data.ratings);
    } catch (error) {
      console.error("Failed to fetch ratings:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logout realizado");
  };

  const getVerificationBadge = () => {
    const status = profile?.verification_status;
    const badges = {
      pending: {
        icon: <Clock className="w-4 h-4" />,
        label: "Pendente",
        color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      },
      verified: {
        icon: <CheckCircle className="w-4 h-4" />,
        label: "Verificado",
        color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      },
      rejected: {
        icon: <XCircle className="w-4 h-4" />,
        label: "Rejeitado",
        color: "bg-red-500/20 text-red-400 border-red-500/30",
      },
      suspended: {
        icon: <XCircle className="w-4 h-4" />,
        label: "Suspenso",
        color: "bg-red-500/20 text-red-400 border-red-500/30",
      },
    };
    return badges[status] || badges.pending;
  };

  const verificationBadge = getVerificationBadge();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 px-4 py-3 bg-black/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white/70"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold">
            {isOwnProfile ? "Meu Perfil" : "Perfil"}
          </h1>
          {isOwnProfile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings")}
              className="text-white/70"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
          )}
        </div>
      </header>

      {/* Profile Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="text-center mb-8">
          {/* Photo Gallery */}
          <div className="flex justify-center gap-2 mb-6">
            {(profile?.profile_photos?.length > 0 || profile?.photos?.length > 0) ? (
              (profile.profile_photos || profile.photos).slice(0, 3).map((photo, index) => {
                const photoUrl = photo.startsWith('http') ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`rounded-2xl overflow-hidden bg-white/10 ${
                      index === 0 ? "w-28 h-36" : "w-20 h-28 opacity-60"
                    }`}
                  >
                    <img
                      src={photoUrl}
                      alt={`Profile ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                );
              })
            ) : (
              <div className="w-28 h-36 rounded-2xl bg-white/10 flex items-center justify-center">
                <User className="w-12 h-12 text-white/30" />
              </div>
            )}
          </div>

          {/* Name & Username */}
          <h2 className="font-heading text-2xl font-bold mb-1">
            {profile?.display_name}
          </h2>
          <p className="text-white/50 mb-4">@{profile?.username}</p>

          {/* Badges */}
          <div className="flex items-center justify-center gap-2 flex-wrap mb-6">
            <Badge
              variant="outline"
              className={verificationBadge.color}
            >
              {verificationBadge.icon}
              <span className="ml-1">{verificationBadge.label}</span>
            </Badge>
            {profile?.is_vip && (
              <Badge className="bg-gold-400 text-black">
                <Star className="w-3 h-3 mr-1" /> VIP
              </Badge>
            )}
            {profile?.is_agency && (
              <Badge className="bg-violet-600">
                <Shield className="w-3 h-3 mr-1" /> Agência
              </Badge>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {profile?.trust_score?.toFixed(1) || "0.0"}
              </div>
              <div className="text-xs text-white/50">Trust Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{profile?.total_ratings || 0}</div>
              <div className="text-xs text-white/50">Avaliações</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold capitalize">{profile?.follower_tier || "micro"}</div>
              <div className="text-xs text-white/50">Tier</div>
            </div>
          </div>
        </div>

        {/* Location */}
        {profile?.location?.city && (
          <div className="glass-card rounded-2xl p-4 mb-4 flex items-center gap-3">
            <MapPin className="w-5 h-5 text-violet-400" />
            <span>
              {profile.location.city}, {profile.location.state}
            </span>
          </div>
        )}

        {/* Bio */}
        {profile?.bio && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-2">Sobre</h3>
            <p className="text-white/80 leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {/* Looking For */}
        {profile?.looking_for && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-2">
              O que busco para collab
            </h3>
            <p className="text-white/80 leading-relaxed">{profile.looking_for}</p>
          </div>
        )}

        {/* Niches */}
        {profile?.niches?.length > 0 && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-3">Nichos</h3>
            <div className="flex flex-wrap gap-2">
              {profile.niches.map((niche) => (
                <span
                  key={niche}
                  className="px-3 py-1 rounded-full bg-[#FD5564]/20 text-[#FD5564] text-sm"
                >
                  {niche}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Social Links */}
        {Object.keys(profile?.social_links || {}).length > 0 && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-3">Redes Sociais</h3>
            <div className="flex flex-wrap gap-3">
              {profile.social_links.instagram && (
                <a
                  href={profile.social_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  <span className="text-sm">Instagram</span>
                  <ExternalLink className="w-3 h-3 text-white/50" />
                </a>
              )}
              {profile.social_links.twitter && (
                <a
                  href={profile.social_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                  <span className="text-sm">Twitter</span>
                  <ExternalLink className="w-3 h-3 text-white/50" />
                </a>
              )}
              {profile.social_links.youtube && (
                <a
                  href={profile.social_links.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <Youtube className="w-4 h-4" />
                  <span className="text-sm">YouTube</span>
                  <ExternalLink className="w-3 h-3 text-white/50" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Platform Links */}
        {Object.keys(profile?.platform_links || {}).length > 0 && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-3">Plataformas</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(profile.platform_links).map(([platform, link]) => (
                <a
                  key={platform}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-full bg-[#FD5564]/20 text-[#FD5564] text-sm font-medium hover:bg-[#FD5564]/30 transition-colors"
                >
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Equipment */}
        {profile?.equipment?.length > 0 && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-3">Equipamentos</h3>
            <div className="flex flex-wrap gap-2">
              {profile.equipment.map((eq, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-sm"
                >
                  {eq}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Ratings */}
        {ratings.length > 0 && (
          <div className="glass-card rounded-2xl p-4 mb-4">
            <h3 className="font-semibold text-white/70 text-sm mb-3">
              Avaliações Recentes
            </h3>
            <div className="space-y-4">
              {ratings.slice(0, 3).map((rating, index) => (
                <div key={index} className="border-b border-white/5 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= rating.average_score
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-white/20"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gold-400">
                      {rating.average_score.toFixed(1)}
                    </span>
                  </div>
                  {rating.comment && (
                    <p className="text-sm text-white/60 italic">"{rating.comment}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {isOwnProfile && (
          <div className="space-y-3 mt-8">
            <Button
              onClick={() => navigate("/onboarding")}
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 h-12 rounded-xl"
              data-testid="edit-profile-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar Perfil
            </Button>
            {currentUser?.is_admin && (
              <Button
                onClick={() => navigate("/admin")}
                className="w-full bg-white hover:bg-white/90 text-black h-12 rounded-xl"
                data-testid="admin-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Painel Admin
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-red-400 hover:bg-red-500/10 h-12 rounded-xl"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default ProfilePage;
